import { SettingsManager as BaseSettingsManager } from '@bn-games/settings-core';
import { CLOCK_PRESETS } from '../game/clock.js';
import {
  DEFAULT_COLORS, DEFAULT_SETTINGS, DIFFICULTIES, LANGUAGES, THEME_NAMES, type Settings,
} from './types.js';

const STORAGE_KEY = 'chess-settings';
export const MAX_NAME_LENGTH = 16;

function oneOf<T extends string>(value: unknown, options: readonly T[], fallback: T): T {
  return options.find((o) => o === value) ?? fallback;
}

function bool(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function positiveNumber(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : fallback;
}

function name(value: unknown, fallback: string): string {
  if (typeof value !== 'string') return fallback;
  const trimmed = value.trim().slice(0, MAX_NAME_LENGTH);
  return trimmed.length > 0 ? trimmed : fallback;
}

export function mergeSettings(stored: Record<string, unknown>, d: Settings): Settings {
  return {
    sound: bool(stored['sound'], d.sound),
    difficulty: oneOf(stored['difficulty'], DIFFICULTIES, d.difficulty),
    defaultColor: oneOf(stored['defaultColor'], DEFAULT_COLORS, d.defaultColor),
    theme: oneOf(stored['theme'], THEME_NAMES, d.theme),
    language: oneOf(stored['language'], LANGUAGES, d.language),
    showValidMoves: bool(stored['showValidMoves'], d.showValidMoves),
    showMoveList: bool(stored['showMoveList'], d.showMoveList),
    playerName: name(stored['playerName'], d.playerName),
    flipBoard: bool(stored['flipBoard'], d.flipBoard),
    afkAssistEnabled: bool(stored['afkAssistEnabled'], d.afkAssistEnabled),
    afkTimeoutMs: positiveNumber(stored['afkTimeoutMs'], d.afkTimeoutMs),
    afkRemindersEnabled: bool(stored['afkRemindersEnabled'], d.afkRemindersEnabled),
    showAfkPingButton: bool(stored['showAfkPingButton'], d.showAfkPingButton),
    turnStartSound: bool(stored['turnStartSound'], d.turnStartSound),
    clockEnabled: bool(stored['clockEnabled'], d.clockEnabled),
    clockPreset: oneOf(stored['clockPreset'], CLOCK_PRESETS, d.clockPreset),
  };
}

export class SettingsManager extends BaseSettingsManager<Settings> {
  constructor() {
    super(STORAGE_KEY, DEFAULT_SETTINGS, mergeSettings);
  }
}
